import { cn } from '@/shared/utils/utils';
import { TaskCard } from './TaskCard';
import type { Task, TaskStatus } from '../types';

const COLUMNS: { status: TaskStatus; label: string; headerClass: string }[] = [
  { status: 'todo', label: 'To Do', headerClass: 'border-outline text-on-surface-variant' },
  { status: 'in_progress', label: 'In Progress', headerClass: 'border-primary/40 text-primary' },
  { status: 'review', label: 'Review', headerClass: 'border-warning/40 text-warning' },
  { status: 'done', label: 'Done', headerClass: 'border-success/40 text-success' },
];

interface TasksKanbanProps {
  tasks: Task[];
  search: string;
  onEdit: (task: Task) => void;
  onDelete: (task: Task) => void;
  onOpenDrawer: (task: Task) => void;
  onStatusChange: (id: string, status: TaskStatus) => void;
}

export function TasksKanban({ tasks, search, onEdit, onDelete, onOpenDrawer, onStatusChange }: TasksKanbanProps) {
  // Client-side search filter on title / assignee / project
  const filtered = search
    ? tasks.filter((t) => {
        const q = search.toLowerCase();
        return (
          t.title.toLowerCase().includes(q) ||
          t.assignee_name?.toLowerCase().includes(q) ||
          t.project_name?.toLowerCase().includes(q)
        );
      })
    : tasks;

  const byStatus = (status: TaskStatus) => filtered.filter((t) => t.status === status);

  return (
    <div className="grid flex-1 grid-cols-1 gap-4 overflow-x-auto sm:grid-cols-2 lg:grid-cols-4">
      {COLUMNS.map(({ status, label, headerClass }) => {
        const col = byStatus(status);
        return (
          <div key={status} className="flex min-h-0 flex-col rounded-card border border-outline bg-surface-container/50">
            {/* Column header */}
            <div className={cn('flex items-center gap-2 border-b px-3 py-2.5', headerClass)}>
              <span className="text-sm font-semibold">{label}</span>
              <span className="ml-auto rounded-full bg-surface-variant px-2 py-0.5 text-[11px] font-bold">
                {col.length}
              </span>
            </div>

            {/* Cards */}
            <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-2">
              {col.length === 0 ? (
                <p className="py-4 text-center text-[11px] text-on-surface-variant/40">No tasks</p>
              ) : (
                col.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onOpenDrawer={onOpenDrawer}
                    onStatusChange={onStatusChange}
                  />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
